import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * "Why now" - the frameworks most customers are measured against (NIS2,
 * ISO 27001, BSI IT-Grundschutz) plus the EU AI Act, all of which make
 * continuous asset inventory + technical evidence mandatory. DORA and CRA were
 * dropped: they fit few customers, and CRA is largely organisational. Creates urgency
 * before the closing CTA. Themed via --ak-* tokens.
 */

const tPrefix = "anketa.autonubil.home.now";

const REGS: { key: string; tag: string; titleFb: string; descFb: string; meta?: string }[] = [
  {
    key: "nis2",
    tag: "NIS2",
    titleFb: "NIS2",
    descFb:
      "Expands critical-infrastructure scope to “essential” and “important” entities - mandating asset inventory, vulnerability and incident management.",
  },
  {
    key: "iso27001",
    tag: "ISO 27001",
    titleFb: "ISO/IEC 27001",
    descFb:
      "Asset inventory (A.5.9) and technical vulnerability management (A.8.8) as auditable controls.",
  },
  {
    key: "bsi",
    tag: "BSI",
    titleFb: "BSI IT-Grundschutz",
    descFb:
      "Structure analysis and protection requirements built on an up-to-date asset inventory.",
  },
  {
    key: "aiact",
    tag: "AI Act",
    titleFb: "EU AI Act",
    descFb:
      "Governance for AI. With Tarnkappe, opsanio uses modern AI on sensitive data in a GDPR-compliant way.",
  },
];

export const AutonubilWhyNow = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  return (
    <div
      style={{
        background: "var(--ak-surface-2)",
        padding: "72px clamp(24px, 5vw, 48px)",
        borderTop: "1px solid var(--ak-border)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ marginBottom: 44, maxWidth: 720 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ak-accent)",
              marginBottom: 10,
            }}
          >
            {translate(`${tPrefix}.eyebrow`, { defaultMessage: "Why now" } as any)}
          </div>
          <div style={{ width: "fit-content", maxWidth: "100%", margin: "0 0 16px" }}>
            <h2
              style={{
                color: "var(--ak-text)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                margin: 0,
                textWrap: "balance",
              }}
            >
              {translate(`${tPrefix}.title`, { defaultMessage: "Compliance is no longer optional." } as any)}
            </h2>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 14,
                background: "linear-gradient(90deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
                borderRadius: 2,
              }}
            />
          </div>
          <p
            style={{
              margin: 0,
              color: "var(--ak-text-secondary)",
              fontSize: "clamp(0.97rem, 1.6vw, 1.1rem)",
              lineHeight: 1.65,
            }}
          >
            {translate(`${tPrefix}.subtitle`, {
              defaultMessage:
                "NIS2, ISO 27001 and BSI IT-Grundschutz demand a complete, provable asset inventory and vulnerability management. opsanio delivers the technical evidence base automatically.",
            } as any)}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 16,
          }}
        >
          {REGS.map((r) => (
            <div
              key={r.key}
              style={{
                background: "var(--ak-surface)",
                border: "1px solid var(--ak-border)",
                borderLeft: "3px solid var(--ak-accent)",
                borderRadius: 12,
                padding: "22px 20px",
                boxShadow: "0 1px 4px var(--ak-shadow)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--ak-accent)",
                    background: "var(--ak-accent-tint)",
                    border: "1px solid var(--ak-accent-border)",
                    borderRadius: 6,
                    padding: "4px 9px",
                  }}
                >
                  {r.tag}
                </span>
                {r.meta && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "var(--ak-text-muted)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {r.meta}
                  </span>
                )}
              </div>
              <h3
                style={{
                  margin: "0 0 8px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "var(--ak-text)",
                }}
              >
                {translate(`${tPrefix}.${r.key}.title`, { defaultMessage: r.titleFb } as any)}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.88rem",
                  lineHeight: 1.6,
                  color: "var(--ak-text-secondary)",
                }}
              >
                {translate(`${tPrefix}.${r.key}.desc`, { defaultMessage: r.descFb } as any)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutonubilWhyNow;
